import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";

import { cx } from "../lib/cx";
import { useTheme } from "../theme/theme-context";
import { CommandPalette } from "../shortcuts/CommandPalette";
import { HelpDialog, type HelpTab } from "../help/HelpDialog";
import { useGlobalShortcuts } from "../shortcuts/useGlobalShortcuts";
import { HelpIcon, SidebarIcon } from "../ui/icons";
import { HeaderSlotContext } from "./header-slot";
import { NewCollectionDialog } from "./NewCollectionDialog";
import { NotificationsBell } from "./NotificationsBell";
import { Sidebar } from "./Sidebar";
import { SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH, useResizableSidebar } from "./useResizableSidebar";
import { ThemeToggle } from "./ThemeToggle";
import { UserMenu } from "./UserMenu";

/**
 * Chrome for every authenticated screen: a persistent, collapsible, resizable
 * sidebar, a header bar that routed screens fill through `PageHeader`, and the
 * application-wide dialogs (shortcuts guide, command menu).
 */
export function AppLayout(): ReactNode {
  const navigate = useNavigate();
  const theme = useTheme();
  const sidebar = useResizableSidebar();
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
  const [helpRequested, setHelpRequested] = useState<HelpTab | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [newCollectionOpen, setNewCollectionOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // `?shortcuts` opens the guide from a link (note 03 §3.2). The dialog's
  // openness is derived from the URL rather than mirrored into state, so the two
  // can never disagree.
  const shortcutsFromUrl = searchParams.has("shortcuts");
  const helpTab: HelpTab | null = helpRequested ?? (shortcutsFromUrl ? "shortcuts" : null);

  const closeHelp = useCallback(() => {
    setHelpRequested(null);
    if (shortcutsFromUrl) {
      const next = new URLSearchParams(searchParams);
      next.delete("shortcuts");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, shortcutsFromUrl]);

  const handlers = useMemo(
    () => ({
      goHome: () => void navigate("/home"),
      goSearch: () => void navigate("/search"),
      newDocument: () => void navigate("/doc/new"),
      openShortcuts: () => {
        setHelpRequested("shortcuts");
      },
      openCommandPalette: () => {
        setPaletteOpen(true);
      },
      toggleSidebar: sidebar.toggleCollapsed,
      toggleTheme: theme.cycle,
    }),
    [navigate, sidebar.toggleCollapsed, theme.cycle],
  );
  useGlobalShortcuts(handlers);

  const onHandleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        sidebar.nudge(-16);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        sidebar.nudge(16);
      }
    },
    [sidebar],
  );

  return (
    <div className="flex min-h-screen bg-canvas text-ink">
      <aside
        aria-label="Sidebar"
        hidden={sidebar.collapsed}
        style={{ width: sidebar.collapsed ? undefined : sidebar.width }}
        className="relative shrink-0 border-r border-line bg-surface"
      >
        <Sidebar
          onNewCollection={() => {
            setNewCollectionOpen(true);
          }}
        />
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          aria-valuenow={sidebar.width}
          aria-valuemin={SIDEBAR_MIN_WIDTH}
          aria-valuemax={SIDEBAR_MAX_WIDTH}
          tabIndex={0}
          onMouseDown={(event) => {
            event.preventDefault();
            sidebar.startResize(event.clientX);
          }}
          onKeyDown={onHandleKeyDown}
          className={cx(
            "absolute top-0 right-0 h-full w-1 cursor-col-resize",
            sidebar.resizing ? "bg-accent" : "hover:bg-accent",
          )}
        />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-line px-4 py-2">
          <button
            type="button"
            onClick={sidebar.toggleCollapsed}
            aria-label={sidebar.collapsed ? "Show sidebar" : "Hide sidebar"}
            aria-pressed={!sidebar.collapsed}
            className="rounded-md p-1.5 text-muted hover:bg-hover"
          >
            <SidebarIcon />
          </button>
          <div ref={setHeaderSlot} className="flex min-w-0 flex-1 items-center gap-3" />
          <button
            type="button"
            onClick={() => {
              setHelpRequested("editing");
            }}
            aria-label="Help"
            title="Help: editing guide and keyboard shortcuts (?)"
            className="rounded-md p-1.5 text-muted hover:bg-hover"
          >
            <HelpIcon />
          </button>
          <ThemeToggle />
          <NotificationsBell />
          <UserMenu />
        </header>

        <main className="min-w-0 flex-1 px-6 py-6">
          <HeaderSlotContext.Provider value={headerSlot}>
            <Outlet />
          </HeaderSlotContext.Provider>
        </main>
      </div>

      <HelpDialog tab={helpTab} onClose={closeHelp} />
      <CommandPalette
        open={paletteOpen}
        onClose={() => {
          setPaletteOpen(false);
        }}
      />
      <NewCollectionDialog
        open={newCollectionOpen}
        onClose={() => {
          setNewCollectionOpen(false);
        }}
      />
    </div>
  );
}
